import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { Translate, TranslatePluralHtml } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { useColor } from '@change-corgi/design-system-legacy/theme';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { useDashboardReskinExperiment } from '../../../../../../../../hooks/useDashboardReskinExperiment';

import { useSpeedometer } from './hooks/useSpeedometer';
import { Speedometer as SpeedometerReskin } from './index_new';

const FULL_ARC_FRACTION = 2 / 3;
const animatePath = keyframes`
  0% {
    stroke-dasharray: 0 10;
  }
  100% {
    stroke-dasharray: ${2 * Math.PI * FULL_ARC_FRACTION} 10;
  }
`;

const TransitionPath = styled('path')`
	animation: ${animatePath} 3s ease-in-out forwards;
	fill: transparent;
	stroke-linecap: round;
	stroke: ${(props) => props.color};
`;

type Point = [number, number];
const fractionToRectangular = (fraction: number): Point => [
	Math.cos(fraction * 2 * Math.PI),
	Math.sin(fraction * 2 * Math.PI),
];
const createPathData = ({
	start: [startX, startY],
	largeArc,
	end: [endX, endY],
}: {
	start: Point;
	largeArc: boolean;
	end: Point;
}) => {
	const radius = 1;
	return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc ? 1 : 0} 1 ${endX} ${endY}`;
};
const createPathDataForProgress = ({ progress }: { progress: number }) => {
	const startFraction = -1 / 4 - FULL_ARC_FRACTION / 2;
	const endFraction = startFraction + progress * FULL_ARC_FRACTION;
	const start = fractionToRectangular(startFraction);
	const end = fractionToRectangular(endFraction);
	const largeArc = endFraction - startFraction >= 0.5;
	return createPathData({ start, largeArc, end });
};
export function Speedometer() {
	const { data } = useSpeedometer();
	const {
		data: { isEnabled },
	} = useDashboardReskinExperiment();

	const greyBackgroundColor = useColor('neutral-grey100');
	const purpleColor = useColor('secondary-purple');
	const strokeWidth = 0.08;

	if (isEnabled) {
		return <SpeedometerReskin />;
	}

	return (
		<>
			<VisuallyHidden>
				<label htmlFor="progress-bar">
					<Translate
						value="corgi.petition.starter_dashboard.petition_overview.a11y.progress_label"
						replacements={{ progress: data.progressPercentage }}
					/>
				</label>
				<progress id="progress-bar" value={data.progressPercentage} max="100">
					{data.progressPercentage}
				</progress>
				<Text>
					<TranslatePluralHtml
						value="corgi.petition.starter_dashboard.petition_overview.progress.supporters"
						count={data.supporters}
						replacements={{ supporterCount: data.supportersFormatted }}
					/>
				</Text>
			</VisuallyHidden>
			<Flex
				sx={{
					alignItems: 'center',
					flexDirection: 'column',
					justifyContent: 'center',
					position: 'relative',
					width: '100%',
				}}
				aria-hidden
				data-testid="speedometer-control"
			>
				<Text
					sx={{
						textAlign: 'center',
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '80%',
						// eslint-disable-next-line @typescript-eslint/naming-convention
						'& > span:first-of-type': { fontSize: 40, fontWeight: 'bold' },
					}}
				>
					<TranslatePluralHtml
						value="corgi.petition.starter_dashboard.petition_overview.progress.supporters"
						count={data.supporters}
						replacements={{ supporterCount: data.supportersFormatted }}
					/>
				</Text>
				<Box sx={{ width: '100%', maxWidth: '240px' }}>
					<svg
						id="my-svg"
						viewBox={`${-1 - strokeWidth / 2} ${-1 - strokeWidth / 2} ${2 + strokeWidth} ${2 + strokeWidth}`}
						sx={{
							backgroundColor: 'transparent',
							display: 'block',
							height: '100%',
						}}
					>
						<path
							sx={{ stroke: greyBackgroundColor, strokeWidth: `${strokeWidth}px` }}
							d={createPathDataForProgress({ progress: 1 })}
							fill="transparent"
							strokeLinecap="round"
						/>
						<TransitionPath
							d={createPathDataForProgress({ progress: data.progress })}
							strokeWidth={`${strokeWidth}px`}
							color={purpleColor}
						/>
						;
					</svg>
				</Box>
			</Flex>
		</>
	);
}
